"use client"

import { useState, useEffect } from "react"
import { Star, MessageCircle } from "lucide-react"
import { Button } from "../components/ui/button"
import { Textarea } from "../components/ui/textarea"
import { ScrollArea } from "../components/ui/scroll-area"
import { useAuth } from "../hooks/useAuth"
import { ref, onValue, update, remove } from "../lib/firebase-database"
import { db, auth } from "../firebase"
import { OwnerLayout } from "../components/OwnerLayout"
import { AnimatePresence, motion } from "framer-motion"
import { useNavigate } from "react-router-dom"
import LogoutConfirmationPopup from "../components/LogoutConfirmationPopup"

// Add the VillaSelector component
const VillaSelector = ({ villas, selectedVillaId, onVillaSelect }) => (
  <div className="flex gap-2 mb-4 md:mb-6 overflow-x-auto pb-2 -mx-1">
    {villas.map((villa) => (
      <Button
        key={villa.id}
        variant={selectedVillaId === villa.id ? "default" : "outline"}
        onClick={() => onVillaSelect(villa.id)}
        className="text-xs md:text-sm whitespace-nowrap px-2 md:px-3 py-1 md:py-2"
      >
        {villa.name}
      </Button>
    ))}
  </div>
)

// Update the CommentText component
const CommentText = ({ comment }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const isLongComment = comment.length > 150 // Reduced from 200 for mobile

  if (!isLongComment) {
    return (
      <div className="mt-2">
        <p className="text-xs md:text-sm text-gray-700 leading-relaxed pr-[10%] md:pr-[20%] text-justify">{comment}</p>
      </div>
    )
  }

  return (
    <div className="mt-2">
      <p className="text-xs md:text-sm text-gray-700 leading-relaxed pr-[10%] md:pr-[20%] text-justify">
        {isExpanded ? comment : `${comment.slice(0, 150)}...`}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="ml-1 text-blue-600 hover:text-blue-800 font-medium text-xs"
        >
          {isExpanded ? "Show Less" : "Read More"}
        </button>
      </p>
      {isExpanded && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-4 md:p-6 rounded-lg max-w-2xl w-full mx-4">
            <p className="text-sm md:text-base text-gray-700 mb-4 max-h-[60vh] overflow-y-auto text-justify">
              {comment}
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setIsExpanded(false)}
                className="px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default function OwnerReviews() {
  const [sortBy, setSortBy] = useState("newest")
  const [filterByRating, setFilterByRating] = useState("all")
  const [replies, setReplies] = useState({})
  const [showReplyBox, setShowReplyBox] = useState({})
  const [reviews, setReviews] = useState([])
  const [ownedVillas, setOwnedVillas] = useState([])
  const [editingReply, setEditingReply] = useState({
    reviewId: null,
    replyId: null,
    text: "",
  })
  const { user } = useAuth()
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [userProfiles, setUserProfiles] = useState({})
  const [userPhotos, setUserPhotos] = useState({})
  const [userNames, setUserNames] = useState({})
  const [selectedVillaId, setSelectedVillaId] = useState(null)
  const navigate = useNavigate()

  const handleLogout = () => {
    setShowLogoutConfirmation(true)
  }

  const confirmLogout = () => {
    auth.signOut().then(() => {
      navigate("/login")
    })
  }

  useEffect(() => {
    if (!user) return

    const villasRef = ref(db, "villas")
    const unsubscribe = onValue(villasRef, (snapshot) => {
      const villasData = snapshot.val()
      if (villasData) {
        const userVillas = Object.entries(villasData)
          .filter(([_, villa]) => villa.ownerEmail === user.email)
          .map(([id, data]) => ({ id, ...data }))
        setOwnedVillas(userVillas)

        userVillas.forEach((villa) => {
          const reviewsRef = ref(db, `reviews/${villa.id}`)
          onValue(reviewsRef, (reviewSnapshot) => {
            const reviewsData = reviewSnapshot.val()
            if (reviewsData) {
              const reviewsList = Object.entries(reviewsData).map(([reviewId, review]) => ({
                id: reviewId,
                villaId: villa.id,
                villaName: villa.name,
                ...review,
              }))
              setReviews((prev) => {
                const filteredPrev = prev.filter((r) => r.villaId !== villa.id)
                return [...filteredPrev, ...reviewsList]
              })
            }
          })
        })
      }
    })

    return () => unsubscribe()
  }, [user])

  useEffect(() => {
    const usersRef = ref(db, "users")
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const users = snapshot.val()
      if (users) {
        setUserProfiles(users)
      }
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    const usersRef = ref(db, "users")
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const users = snapshot.val()
      if (users) {
        const photos = {}
        Object.entries(users).forEach(([userId, userData]) => {
          if (userData.photoURL) {
            photos[userId] = userData.photoURL
          }
        })
        setUserPhotos(photos)
      }
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    const usersRef = ref(db, "users")
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const users = snapshot.val()
      if (users) {
        const names = {}
        Object.entries(users).forEach(([userId, userData]) => {
          if (userData.displayName) {
            names[userId] = userData.displayName
          }
        })
        setUserNames(names)
      }
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    if (!user || !selectedVillaId) return

    const reviewsRef = ref(db, `reviews/${selectedVillaId}`)
    const unsubscribe = onValue(reviewsRef, (snapshot) => {
      const reviewsData = snapshot.val()
      if (reviewsData) {
        const reviewsList = Object.entries(reviewsData).map(([reviewId, review]) => ({
          id: reviewId,
          villaId: selectedVillaId,
          ...review,
        }))
        setReviews(reviewsList)
      } else {
        setReviews([])
      }
    })

    return () => unsubscribe()
  }, [user, selectedVillaId])

  const handleReply = (reviewId) => {
    if (!replies[reviewId]) return

    const review = reviews.find((r) => r.id === reviewId)
    if (!review) return

    const reviewRef = ref(db, `reviews/${review.villaId}/${reviewId}/replies`)
    const timestamp = Date.now()

    update(reviewRef, {
      [timestamp]: {
        text: replies[reviewId],
        userId: user.uid,
        userName: user.displayName,
        userPhoto: user.photoURL,
        createdAt: timestamp,
      },
    }).then(() => {
      setShowReplyBox({ ...showReplyBox, [reviewId]: false })
      setReplies({ ...replies, [reviewId]: "" })
    })
  }

  const handleDeleteReply = (reviewId, replyId) => {
    const review = reviews.find((r) => r.id === reviewId)
    if (!review) return

    const reviewRef = ref(db, `reviews/${review.villaId}/${reviewId}/replies/${replyId}`)
    remove(reviewRef)
  }

  const handleEditReply = (reviewId, replyId) => {
    const review = reviews.find((r) => r.id === reviewId)
    if (!review) return

    const reviewRef = ref(db, `reviews/${review.villaId}/${reviewId}/replies/${replyId}`)
    update(reviewRef, {
      text: editingReply.text,
      editedAt: Date.now(),
    }).then(() => {
      setEditingReply({ reviewId: null, replyId: null, text: "" })
    })
  }

  const sortedReviews = [...reviews].sort((a, b) => {
    switch (sortBy) {
      case "newest":
        return b.createdAt - a.createdAt
      case "oldest":
        return a.createdAt - b.createdAt
      case "rating-high":
        return b.rating - a.rating
      case "rating-low":
        return a.rating - b.rating
      default:
        return 0
    }
  })

  const filteredReviews = sortedReviews.filter((review) => {
    if (filterByRating === "all") return true
    return review.rating === Number.parseInt(filterByRating)
  })

  return (
    <OwnerLayout user={user} onLogout={handleLogout}>
      <div className="min-h-screen bg-gray-50 md:bg-white flex flex-col">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-4 md:py-8 w-full">
          <div className="sticky top-0 z-20 bg-gray-50 md:bg-white pb-4">
            <div className="mb-4 md:mb-8">
              <h1 className="text-2xl lg:text-4xl font-bold mb-2">Reviews</h1>
              <p className="text-base lg:text-xl text-gray-600">Manage your reviews</p>
            </div>
            <VillaSelector villas={ownedVillas} selectedVillaId={selectedVillaId} onVillaSelect={setSelectedVillaId} />
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b pb-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full sm:w-48 p-2 border rounded-lg text-xs md:text-sm bg-white shadow-sm"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="rating-high">Highest Rating</option>
                <option value="rating-low">Lowest Rating</option>
              </select>
            </div>
          </div>

          {/* Reviews List */}
          <ScrollArea className="h-[calc(100vh-16rem)] rounded-lg mb-16 lg:mb-0">
            <div className="space-y-3 md:space-y-4 pb-6 reviews-container">
              <AnimatePresence>
                {filteredReviews.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-center text-gray-500 py-8"
                  >
                    No reviews found
                  </motion.div>
                ) : (
                  filteredReviews.map((review) => (
                    <motion.div
                      key={review.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2 }}
                      className="bg-white rounded-lg shadow-sm p-3 md:p-4"
                    >
                      {/* Review Header - Updated layout */}
                      <div className="flex items-start gap-2 md:gap-3">
                        <img
                          src={userPhotos[review.userId] || review.userPhoto || "/placeholder.svg"}
                          alt={userNames[review.userId] || review.userName}
                          className="w-8 h-8 md:w-10 md:h-10 rounded-full flex-shrink-0"
                        />
                        <div className="flex-1">
                          <div className="flex flex-col">
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="font-medium text-sm md:text-lg">
                                  {userNames[review.userId] || review.userName}
                                </h3>
                                <p className="text-xs md:text-sm text-gray-500 mt-0.5">{review.villaName}</p>
                              </div>
                            </div>
                            <div className="flex gap-1 mt-1 md:mt-2">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`h-3 w-3 md:h-5 md:w-5 ${
                                    star <= review.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-200"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>

                          {/* Review Comment */}
                          <CommentText comment={review.comment} />

                          {/* Reply Button - Conditionally Rendered */}
                          <div className="mt-3 md:mt-4 flex justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setShowReplyBox({ ...showReplyBox, [review.id]: !showReplyBox[review.id] })
                              }
                              className="h-7 md:h-8 px-3 md:px-4 text-xs md:text-sm hover:bg-white hover:text-black transition-colors"
                            >
                              <MessageCircle className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                              Reply
                            </Button>
                          </div>

                          {/* Reply Box and Replies - Keep same left margin */}
                          <div className="ml-0">
                            <AnimatePresence>
                              {showReplyBox[review.id] && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  className="mt-2 md:mt-3 space-y-2"
                                >
                                  <Textarea
                                    placeholder="Write your reply..."
                                    value={replies[review.id] || ""}
                                    onChange={(e) => setReplies({ ...replies, [review.id]: e.target.value })}
                                    className="min-h-[60px] text-xs md:text-sm resize-none"
                                  />
                                  <div className="flex gap-2">
                                    <Button
                                      size="sm"
                                      onClick={() => handleReply(review.id)}
                                      className="h-7 md:h-8 text-xs md:text-sm"
                                    >
                                      Reply
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setShowReplyBox({ ...showReplyBox, [review.id]: false })}
                                      className="h-7 md:h-8 text-xs md:text-sm"
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>

                            {/* Replies */}
                            {review.replies && (
                              <div className="mt-2 md:mt-3 space-y-2">
                                {Object.entries(review.replies).map(([replyId, reply]) => (
                                  <div key={replyId} className="bg-gray-50 rounded-md p-2 md:p-3">
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex items-center gap-2">
                                        <img
                                          src={userPhotos[reply.userId] || reply.userPhoto || "/placeholder.svg"}
                                          alt={userNames[reply.userId] || reply.userName}
                                          className="w-5 h-5 md:w-6 md:h-6 rounded-full"
                                        />
                                        <div>
                                          <p className="text-xs md:text-sm font-medium">
                                            {userNames[reply.userId] || reply.userName}
                                            {user?.uid === reply.userId && " (Owner)"}
                                          </p>
                                          <p className="text-[10px] md:text-xs text-gray-500">
                                            {new Date(reply.createdAt).toLocaleDateString()}
                                            {reply.editedAt && " • edited"}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex gap-1">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            setEditingReply({
                                              reviewId: review.id,
                                              replyId,
                                              text: reply.text,
                                            })
                                          }
                                          className="h-5 md:h-6 px-1 md:px-2 text-[10px] md:text-xs"
                                        >
                                          Edit
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-5 md:h-6 px-1 md:px-2 text-[10px] md:text-xs text-red-600"
                                          onClick={() => handleDeleteReply(review.id, replyId)}
                                        >
                                          Delete
                                        </Button>
                                      </div>
                                    </div>
                                    <p className="text-xs md:text-sm mt-1 text-gray-700">{reply.text}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </AnimatePresence>
            </div>
          </ScrollArea>
        </div>
      </div>
      {showLogoutConfirmation && (
        <LogoutConfirmationPopup onConfirm={confirmLogout} onCancel={() => setShowLogoutConfirmation(false)} />
      )}
    </OwnerLayout>
  )
}

